import React, { useState, useEffect, useRef, useCallback } from "react";
import ArticuloService from "../../services/articulo.service";
import { useCart } from "react-use-cart";
import { toast } from "react-toastify";
import { Container, Button } from "react-bootstrap/";
import { useMediaQuery } from "react-responsive";
import Lottie from "react-lottie";
import * as lapiz from "../../assets/reloj.json";

import SearchIcon from "@mui/icons-material/Search";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import logo from "../../assets/iconCamara32.png";

import "./tienda.css";
import { useNavigate } from "react-router-dom";
import EventBus from "../../common/EventBus";
import ProductoCard from "./ProductoCard";

import * as FileSaver from "file-saver";
import * as XLSX from "xlsx";
const dayjs = require("dayjs");

const ITEMS_PER_PAGE = 24;
const TIENDA_SEARCH_KEY = "tienda_search";

// ✅ Backend paginado listo - usar endpoints nuevos
const USE_SERVER_PAGINATION = true;

const ArticulosTienda = (tipo) => {
  const [arts, setArts] = useState([]);
  const [filteredArts, setFilteredArts] = useState([]);
  const [displayedArts, setDisplayedArts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [search, setSearch] = useState(() => {
    try {
      return sessionStorage.getItem(TIENDA_SEARCH_KEY) || "";
    } catch {
      return "";
    }
  });
  const [oferta] = useState(tipo.children);
  const navigate = useNavigate();
  const [exporting, setExporting] = useState(false);
  const isTabletOrMobile = useMediaQuery({ maxWidth: 1224 });
  const sentinelRef = useRef(null);
  const searchInputRef = useRef(null);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(USE_SERVER_PAGINATION ? 0 : 1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalResults, setTotalResults] = useState(0);
  const isSearchingQuery = search.trim().length > 0;

  const { addItem } = useCart();

  const lapízOptions = {
    loop: true,
    autoplay: true,
    animationData: lapiz.default,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice",
    },
  };

  // ========================
  // MODO CLIENTE (actual)
  // ========================
  const loadClientMode = useCallback(async () => {
    setLoading(true);
    try {
      let response;
      if (oferta === 1) {
        response = await ArticuloService.getOfertas();
      } else {
        response = await ArticuloService.getTodos();
      }
      const data = response?.data || [];
      setArts(data);
      setFilteredArts(data);
      setDisplayedArts(data.slice(0, ITEMS_PER_PAGE));
      setHasMore(data.length > ITEMS_PER_PAGE);
      setPage(1);
    } catch (error) {
      console.error(error);
      if (error.response && error.response.status === 401) {
        EventBus.dispatch("logout");
      }
      setArts([]);
      setFilteredArts([]);
      setDisplayedArts([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [oferta]);

  // ========================
  // MODO SERVIDOR (futuro)
  // ========================
  const loadServerPage = useCallback(
    async (pageNum, searchTerm = "", append = false) => {
      try {
        let response;
        if (oferta === 1) {
          response = await ArticuloService.getOfertasPaginado(
            pageNum,
            ITEMS_PER_PAGE,
            searchTerm
          );
        } else {
          response = await ArticuloService.getPaginado(
            pageNum,
            ITEMS_PER_PAGE,
            searchTerm
          );
        }
        const { content, totalElements, totalPages: total, number } = response.data;

        if (append) {
          setDisplayedArts((prev) => [...prev, ...content]);
        } else {
          setDisplayedArts(content);
        }
        setTotalPages(total);
        setTotalResults(totalElements);
        setPage(number + 1);
        setHasMore(number + 1 < total);
      } catch (error) {
        console.error(error);
        if (error.response && error.response.status === 401) {
          EventBus.dispatch("logout");
        }
        setHasMore(false);
      }
    },
    [oferta]
  );

  // Carga inicial
  useEffect(() => {
    if (USE_SERVER_PAGINATION) {
      setLoading(true);
      loadServerPage(0, search).finally(() => setLoading(false));
    } else {
      loadClientMode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oferta, loadClientMode, loadServerPage]);

  // Filtrado / búsqueda (modo cliente: filtrado local inmediato, sin debounce de carga)
  useEffect(() => {
    if (USE_SERVER_PAGINATION) return; // En modo servidor, la búsqueda se maneja por debounce separado

    if (!search.trim()) {
      setFilteredArts(arts);
      setDisplayedArts(arts.slice(0, ITEMS_PER_PAGE));
      setHasMore(arts.length > ITEMS_PER_PAGE);
      setPage(1);
      setTotalResults(arts.length);
      return;
    }
    const term = search.toLowerCase();
    const filtered = arts.filter((art) => {
      return (
        (art.descri && art.descri.toLowerCase().includes(term)) ||
        (art.codigo && art.codigo.toLowerCase().includes(term)) ||
        (art.codi_barra && art.codi_barra.toLowerCase().includes(term))
      );
    });
    setFilteredArts(filtered);
    setDisplayedArts(filtered.slice(0, ITEMS_PER_PAGE));
    setHasMore(filtered.length > ITEMS_PER_PAGE);
    setPage(1);
    setTotalResults(filtered.length);
  }, [search, arts]);

  // Debounce de búsqueda para modo servidor (sin recrear el componente)
  const searchTimeoutRef = useRef(null);

  const handleSearchChange = (value) => {
    setSearch(value);

    try {
      sessionStorage.setItem(TIENDA_SEARCH_KEY, value);
    } catch {
      // ignore
    }

    if (!USE_SERVER_PAGINATION) return;

    // Cancelar timeout anterior
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Debounce de 400ms para evitar llamadas excesivas
    searchTimeoutRef.current = setTimeout(() => {
      setSearching(true);
      loadServerPage(0, value).finally(() => {
        setSearching(false);
        // Restaurar foco al input de búsqueda
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      });
    }, 400);
  };

  // Cleanup del timeout al desmontar
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      // Si nos vamos a una ruta que NO sea tienda, limpiamos la búsqueda guardada
      const nextPath = window.location.pathname;
      if (!nextPath.startsWith("/tienda")) {
        try {
          sessionStorage.removeItem(TIENDA_SEARCH_KEY);
        } catch {
          // ignore
        }
      }
    };
  }, []);

  // Cargar más items (infinite scroll)
  const loadMore = useCallback(() => {
    if (!hasMore || loading) return;

    if (USE_SERVER_PAGINATION) {
      loadServerPage(page, search, true);
    } else {
      const nextPage = page + 1;
      const start = (nextPage - 1) * ITEMS_PER_PAGE;
      const end = nextPage * ITEMS_PER_PAGE;
      const nextItems = filteredArts.slice(start, end);

      if (nextItems.length === 0) {
        setHasMore(false);
        return;
      }

      setDisplayedArts((prev) => [...prev, ...nextItems]);
      setPage(nextPage);
      setHasMore(end < filteredArts.length);
    }
  }, [hasMore, loading, page, search, filteredArts, loadServerPage]);

  // IntersectionObserver para infinite scroll
  useEffect(() => {
    if (!sentinelRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { rootMargin: "200px" }
    );

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [loadMore]);

  const handleCardClick = (articulo) => {
    navigate(`/tienda/${articulo.articulos_id}`, { state: { articulo } });
  };

  const handleQuickAdd = (articulo, precioFinal) => {
    const product = {
      id: articulo.articulos_id,
      name: articulo.descri,
      price: articulo.PRE_VTA,
      observacion: "",
    };
    addItem(product, 1);
    toast.success(
      <div>
        Artículo {articulo.descri}
        <br />
        Cantidad: 1 <br /> Agregado correctamente
      </div>,
      {
        position: "top-center",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        whiteSpace: "pre-line",
      }
    );
  };

  const limpiarSearch = () => {
    handleSearchChange("");
  };

  const exportarAExcel = async () => {
    setExporting(true);
    try {
      const arts = await ArticuloService.getTodosSinObservacion();
      const rows = await arts.data;
      const fileType =
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
      const fileExtension = ".xlsx";
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
      const max_widthC1 = rows.reduce(
        (w, r) => Math.max(w, (r.Código || "").length),
        10
      );
      const max_widthC2 = rows.reduce(
        (w, r) => Math.max(w, (r.Descripción || r.Descri || "").length),
        10
      );
      const max_widthC3 = rows.reduce(
        (w, r) => Math.max(w, (r.Código_de_Barras || "").length),
        10
      );
      ws["!cols"] = [
        { wch: max_widthC1 },
        { wch: max_widthC2 },
        { wch: max_widthC3 },
      ];
      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const data = new Blob([excelBuffer], { type: fileType });
      FileSaver.saveAs(
        data,
        "Lista de Precios Karam " +
          dayjs().format("DD/MM/YYYY H:m:ss") +
          fileExtension
      );
    } catch (error) {
      console.log(error);
    } finally {
      setExporting(false);
    }
  };

  // Loading inicial (mientras carga la API)
  if (loading) {
    return (
      <Container fluid className="tienda-container">
        <div className="tienda-loading-global">
          <Lottie options={lapízOptions} height={200} width={200} />
          <p className="tienda-loading-text">Cargando artículos...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="tienda-container">
      {/* Barra de búsqueda estilo e-commerce */}
      <div className="tienda-search-bar">
        <div className="tienda-search-wrapper">
          <SearchIcon className="tienda-search-icon" />
          <input
            ref={searchInputRef}
            type="text"
            className="tienda-search-input"
            placeholder="Buscar artículos..."
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
          {search && (
            <button
              className="tienda-search-clear"
              onClick={limpiarSearch}
              title="Limpiar"
            >
              <ClearAllIcon />
            </button>
          )}
        </div>

        {isTabletOrMobile && (
          <Button
            variant="light"
            className="tienda-action-btn tienda-scan-btn"
            onClick={() => {
              toast.info("Escanear código: próximamente", {
                position: "top-center",
                autoClose: 2000,
              });
            }}
          >
            <img src={logo} alt="Escanear" className="tienda-scan-img" />
            <span>Escanear</span>
          </Button>
        )}

        <Button
          variant="light"
          className="tienda-action-btn tienda-export-btn"
          onClick={exportarAExcel}
          disabled={exporting}
        >
          <FileDownloadIcon />
          <span>{exporting ? "Exportando..." : "Exportar Excel"}</span>
        </Button>
      </div>

      {/* Resultados y contador */}
      {isSearchingQuery && (
        <div className="tienda-results-count">
          {totalResults} resultado{totalResults !== 1 ? "s" : ""} para "{search}"
        </div>
      )}

      {/* Grid de productos */}
      <div className="tienda-grid">
        {displayedArts.length === 0 ? (
          <div className="tienda-empty">
            <p>No se encontraron artículos</p>
          </div>
        ) : (
          displayedArts.map((art) => (
            <ProductoCard
              key={art.articulos_id}
              articulo={art}
              onClick={handleCardClick}
              onAddToCart={handleQuickAdd}
            />
          ))
        )}
      </div>

      {/* Sentinel para infinite scroll */}
      {hasMore && displayedArts.length > 0 && (
        <div ref={sentinelRef} className="tienda-sentinel">
          <div className="tienda-spinner" />
          <span className="tienda-sentinel-text">Cargando más...</span>
        </div>
      )}

      {!hasMore && displayedArts.length > 0 && !search && (
        <div className="tienda-no-more">
          No hay más artículos
        </div>
      )}
    </Container>
  );
};

export default ArticulosTienda;
